/**
 * Synchronous QR output for email HTML export.
 * Uses qrcode internals so generateEmailHTML stays synchronous (no third-party image host).
 */

// eslint-disable-next-line @typescript-eslint/no-var-requires
const QRCodeCore = require('qrcode/lib/core/qrcode');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const SvgTagRenderer = require('qrcode/lib/renderer/svg-tag');

export function normalizeHexColorForQr(input: string | undefined, fallback: string): string {
  const raw = (input || fallback).trim();
  if (!raw) return fallback;
  if (raw.startsWith('#')) {
    let h = raw.slice(1);
    if (/^[0-9a-fA-F]{3}$/.test(h)) {
      h = h
        .split('')
        .map((c: string) => c + c)
        .join('');
    }
    if (/^[0-9a-fA-F]{6}$/.test(h)) {
      return `#${h}`.toUpperCase();
    }
    if (/^[0-9a-fA-F]{8}$/.test(h)) {
      return `#${h.slice(0, 6)}`.toUpperCase();
    }
  }
  const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const toHex = (n: string) =>
      Math.max(0, Math.min(255, parseInt(n, 10)))
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`.toUpperCase();
  }
  return fallback;
}

export function buildQrCodeDataUrlForEmail(
  payload: string,
  options: { displayWidth: number; foregroundColor: string; backgroundColor: string }
): string | null {
  const text = (payload || '').trim() || 'https://example.com';
  const w = Math.min(400, Math.max(50, Math.round(options.displayWidth || 150)));
  const fg = normalizeHexColorForQr(options.foregroundColor, '#000000');
  const bg = normalizeHexColorForQr(options.backgroundColor, '#FFFFFF');
  try {
    const qrData = QRCodeCore.create(text, { errorCorrectionLevel: 'M' });
    const svg = SvgTagRenderer.render(qrData, {
      margin: 1,
      width: w,
      color: {
        dark: `${fg}ff`,
        light: `${bg}ff`,
      },
    });
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  } catch {
    return null;
  }
}
